import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { EditServiceInfoModal } from '@/components/EditServiceInfoModal';
import { AddEditResourceModal, ServiceResource } from '@/components/AddEditResourceModal';
import { AddEditEndpointModal, ServiceEndpoint } from '@/components/AddEditEndpointModal';
import { authorizedFetch, json } from '@nimbitlabs/ui';
import { usePermissions } from '@nimbitlabs/features';
import { api } from '@/lib/api';
import { dependenciesApi } from '@/lib/dependencies';
import { useServicesStore } from '@/stores/servicesStore';
import { useDeploymentsStore } from '@/stores/deploymentsStore';
import { useEnvironmentsStore } from '@/stores/environmentsStore';
import { useMonitoringConfigsStore } from '@/stores/monitoringConfigsStore';
import { useServiceEnvironmentsStore } from '@/stores/serviceEnvironmentsStore';
import { ManageServiceDependenciesModal } from '@/components/ManageServiceDependenciesModal';
import * as API from '@/types/api';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Collapsible, CollapsibleContent, CollapsibleTrigger, TagInput, ErrorAlert } from '@nimbitlabs/ui';
import { 
  ArrowLeft,
  ExternalLink,
  GitBranch,
  FileText,
  CheckCircle,
  AlertTriangle,
  Clock,
  Package,
  Activity,
  Link as LinkIcon,
  Edit,
  Settings,
  Globe,
  Database,
  Shield,
  BarChart3,
  MessageSquare,
  Plus,
  Trash2,
  ChevronDown,
} from 'lucide-react';
import type { 
  ServiceDto, 
  ServiceStatus, 
  ServiceType,
  ServiceMonitoringConfigurationDto,
  DeploymentDto,
  ServiceHealthStatus,
  DeploymentStatus
} from '@/types/api';

type UpdateServiceDtoLocal = {
  name?: string | null;
  description?: string | null;
  version?: string | null;
  status?: ServiceStatus;
  tags?: string[] | null;
  consumers?: string[] | null;
};

const serviceStatusLabel = (s: ServiceStatus): 'healthy' | 'warning' | 'critical' | 'unknown' => {
  switch (s) {
    case 'Healthy': return 'healthy';
    case 'Warning': return 'warning';
    case 'Error': return 'critical';
    case 'Maintenance': return 'unknown';
    default: return 'unknown';
  }
};

const healthStatusLabel = (s: ServiceHealthStatus): 'healthy' | 'critical' | 'unknown' => {
  switch (s) {
    case 'Healthy': return 'healthy';
    case 'Unhealthy':
    case 'Degraded':
    case 'Timeout':
    case 'ConnectionFailure':
    case 'DnsFailure':
    case 'SslFailure':
      return 'critical';
    case 'Unknown':
    default:
      return 'unknown';
  }
};

const typeLabel = (t: ServiceType): string => {
  const map: Record<ServiceType, string> = {
    'Api': 'API',
    'Microservice': 'Microservice',
    'Library': 'Library',
    'Database': 'Database',
    'Frontend': 'Frontend',
  };
  return map[t] ?? t;
};

const formatIsoDate = (iso?: string | null) => (iso ? new Date(iso).toLocaleString() : '-');

export const ServiceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { hasPermission } = usePermissions();
  
  // Zustand stores
  const { services, getServiceById, updateService: updateServiceInStore } = useServicesStore();
  const { deployments } = useDeploymentsStore();
  const { environments } = useEnvironmentsStore();
  const { configs: allMonitoringConfigs } = useMonitoringConfigsStore();
  const { serviceEnvironments: allServiceEnvironments, getByServiceId } = useServiceEnvironmentsStore();
  
  // Get service data from store
  const [serviceLoading, setServiceLoading] = useState(true);
  const [serviceError, setServiceError] = useState<Error | null>(null);
  
  // Get service directly from store instead of local state
  const service = services.find(s => s.id === id) || null;
  
  useEffect(() => {
    const loadService = async () => {
      if (!id) return;
      setServiceLoading(true);
      try {
        await getServiceById(id);
        setServiceError(null);
      } catch (e) {
        setServiceError(e as Error);
      } finally {
        setServiceLoading(false);
      }
    };
    loadService();
  }, [id, getServiceById]);
  
  // Filter data for this service
  const serviceDeployments = deployments.filter(d => d.serviceId === id);
  const serviceEnvironments = getByServiceId(id || '');
  const health = allMonitoringConfigs.filter(config => config.serviceId === id);
  
  const [serviceDependencies, setServiceDependencies] = useState<API.ServiceDependencyDto[]>([]);
  const [serviceDependents, setServiceDependents] = useState<API.ServiceDependencyDto[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [err, setErr] = useState<unknown>(serviceError);

  const [isEditingTags, setIsEditingTags] = useState(false);
  const [isEditInfoModalOpen, setIsEditInfoModalOpen] = useState(false);
  const [isAddResourceModalOpen, setIsAddResourceModalOpen] = useState(false);
  const [isEditResourceModalOpen, setIsEditResourceModalOpen] = useState(false);
  const [editingResource, setEditingResource] = useState<ServiceResource | null>(null);
  const [isDependenciesModalOpen, setIsDependenciesModalOpen] = useState(false);
  const [isManageDependenciesModalOpen, setIsManageDependenciesModalOpen] = useState(false);
  const [isAddEndpointModalOpen, setIsAddEndpointModalOpen] = useState(false);
  const [isEditEndpointModalOpen, setIsEditEndpointModalOpen] = useState(false);
  const [editingEndpoint, setEditingEndpoint] = useState<ServiceEndpoint | null>(null);

  // Fetch dependencies and dependents
  const fetchDependencies = useCallback(async () => {
    if (!id) return;
    
    setLoading(true);
    try {
      const allDeps = await authorizedFetch('/dependencies');
      const allDependencies = await json<API.ServiceDependencyDto[]>(allDeps);
      
      // Filter dependencies (services this service depends on)
      const dependencies = allDependencies.filter(d => d.serviceId === id);
      console.log('Filtered dependencies (this service depends on):', dependencies);
      
      // Filter dependents (services that depend on this service)
      const dependents = allDependencies.filter(d => d.dependsOnServiceId === id);
      console.log('Filtered dependents (depend on this service):', dependents);

      setServiceDependencies(dependencies);
      setServiceDependents(dependents);
    } catch (e: any) {
      console.error('Failed to fetch dependencies:', e);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDependencies();
  }, [fetchDependencies]);

  // Refetch function for modals
  const fetchAll = useCallback(async () => {
    if (!id) return;
    
    // Refetch service data from store
    try {
      await getServiceById(id);
      setServiceError(null);
    } catch (e) {
      setServiceError(e as Error);
    }
    
    // Refetch dependencies
    await fetchDependencies();
  }, [id, getServiceById, fetchDependencies]);

  // Use resources directly from the service
  const serviceResources: ServiceResource[] = useMemo(() => {
    if (!service || !service.resources) return [];
    return service.resources
      .filter(r => r && r.name && r.url) // Filter out invalid resources
      .map((r, index) => ({
        id: `resource-${index}`,
        name: r.name,
        url: r.url,
        type: r.type as ServiceResource['type'],
        description: r.description
      }));
  }, [service]);

  const handleAddResource = async (resourceData: Omit<ServiceResource, 'id'>) => {
    if (!service) return;
    
    try {
      // Filter out empty resources before adding new one
      const validResources = (service.resources || []).filter(r => r && r.name && r.url);
      const updatedResources = [
        ...validResources,
        { name: resourceData.name, url: resourceData.url, type: resourceData.type, description: resourceData.description }
      ];
      
      // Update via store for seamless state management
      await updateServiceInStore(service.id, { resources: updatedResources });
    } catch (err) {
      console.error('✗ Failed to add resource:', err);
    }
  };
  
  const handleEditResource = async (resourceData: Omit<ServiceResource, 'id'> & { id?: string }) => {
    if (!service || !resourceData.id) return;
    
    try {
      // Filter out empty resources first
      const validResources = (service.resources || []).filter(r => r && r.name && r.url);
      
      // Find the resource index by id (based on valid resources only)
      const resourceIndex = parseInt(resourceData.id.replace('resource-', ''));
      
      if (isNaN(resourceIndex) || resourceIndex >= validResources.length) {
        console.error('Invalid resource index');
        return;
      }
      
      // Update the resource in the array
      const updatedResources = [...validResources];
      updatedResources[resourceIndex] = {
        name: resourceData.name,
        url: resourceData.url,
        type: resourceData.type,
        description: resourceData.description
      };
      
      // Update via store for seamless state management
      await updateServiceInStore(service.id, { resources: updatedResources });
    } catch (err) {
      console.error('✗ Failed to update resource:', err);
    }
  };
  
  const handleDeleteResource = async (resourceId: string) => {
    if (!service) return;
    
    try {
      // Filter out empty resources first
      const validResources = (service.resources || []).filter(r => r && r.name && r.url);
      
      // Find the resource index by id (based on valid resources only)
      const resourceIndex = parseInt(resourceId.replace('resource-', ''));
      
      if (isNaN(resourceIndex) || resourceIndex >= validResources.length) {
        console.error('Invalid resource index');
        return;
      }
      
      // Remove the resource from the array
      const updatedResources = validResources.filter((_, index) => index !== resourceIndex);
      
      // Update via store for seamless state management (store handles optimistic updates)
      await updateServiceInStore(service.id, { resources: updatedResources });
    } catch (err) {
      console.error('✗ Failed to delete resource:', err);
    }
  };
  
  const openEditResource = (resource: ServiceResource) => {
    console.log('Opening edit modal for resource:', resource);
    setEditingResource(resource);
    setIsEditResourceModalOpen(true);
  };

  // Build endpoints list from ServiceEnvironments
  const serviceEndpoints: ServiceEndpoint[] = useMemo(() => {
    const endpoints: ServiceEndpoint[] = [];
    
    for (const se of serviceEnvironments) {
      if (!se.environmentName) continue;
      for (const endpoint of se.endpoints) {
        endpoints.push({
          id: endpoint.id,
          type: endpoint.type as ServiceEndpoint['type'],
          url: endpoint.url,
          name: endpoint.name,
          environment: se.environmentName.toLowerCase() as any,
        });
      }
    }
    
    return endpoints;
  }, [serviceEnvironments]);

  const handleAddEndpoint = async (endpointData: Omit<ServiceEndpoint, 'id'>) => {
    if (!service) return;
    
    const envName = endpointData.environment;
    const environment = environments.find(e => e.name?.toLowerCase() === envName?.toLowerCase());
    if (!environment) {
      console.error('Environment not found:', envName);
      return;
    }

    const newEndpoint = { ...endpointData, id: `ep-${Date.now()}` };
    let serviceEnv = serviceEnvironments.find(se => se.environmentId === environment.id);
    
    try {
      if (!serviceEnv) {
        // Create new ServiceEnvironment
        serviceEnv = await api.serviceEnvironments.create({
          serviceId: service.id,
          environmentId: environment.id,
          endpoints: [newEndpoint]
        });
        setServiceEnvironments([...serviceEnvironments, serviceEnv]);
      } else {
        // Update existing ServiceEnvironment
        const updated = await api.serviceEnvironments.updateEndpoints(serviceEnv.id, {
          endpoints: [...serviceEnv.endpoints, newEndpoint]
        });
        setServiceEnvironments(serviceEnvironments.map(se => 
          se.id === updated.id ? updated : se
        ));
      }
      setIsAddEndpointModalOpen(false);
      
      // Refresh all data
      await fetchAll();
    } catch (error) {
      console.error('Failed to add endpoint:', error);
    }
  };

  const handleEditEndpoint = async (endpointData: Omit<ServiceEndpoint, 'id'> & { id?: string }) => {
    if (!endpointData.id || !service) return;
    
    const envName = endpointData.environment;
    const environment = environments.find(e => e.name?.toLowerCase() === envName?.toLowerCase());
    if (!environment) return;

    const serviceEnv = serviceEnvironments.find(se => se.environmentId === environment.id);
    if (!serviceEnv) return;
    
    try {
      const updated = await api.serviceEnvironments.updateEndpoints(serviceEnv.id, {
        endpoints: serviceEnv.endpoints.map(ep => 
          ep.id === endpointData.id ? { ...endpointData, id: endpointData.id } : ep
        )
      });
      setServiceEnvironments(serviceEnvironments.map(se => 
        se.id === updated.id ? updated : se
      ));
      setIsEditEndpointModalOpen(false);
      setEditingEndpoint(null);
      
      // Refresh all data
      await fetchAll();
    } catch (error) {
      console.error('Failed to edit endpoint:', error);
    }
  };

  const handleDeleteEndpoint = async (endpointId: string) => {
    if (!service) return;
    
    const serviceEnv = serviceEnvironments.find(se => 
      se.endpoints.some(ep => ep.id === endpointId)
    );
    if (!serviceEnv) return;
    
    const remainingEndpoints = serviceEnv.endpoints.filter(ep => ep.id !== endpointId);
    
    try {
      // Optimistically update local state
      if (remainingEndpoints.length === 0) {
        setServiceEnvironments(serviceEnvironments.filter(se => se.id !== serviceEnv.id));
        await api.serviceEnvironments.delete(serviceEnv.id);
      } else {
        setServiceEnvironments(serviceEnvironments.map(se => 
          se.id === serviceEnv.id ? { ...se, endpoints: remainingEndpoints } : se
        ));
        await api.serviceEnvironments.updateEndpoints(serviceEnv.id, {
          endpoints: remainingEndpoints
        });
      }
      
      // Refresh all data
      await fetchAll();
    } catch (error) {
      console.error('Failed to delete endpoint:', error);
      // Revert optimistic update on error
      await fetchAll();
    }
  };

  const openEditEndpoint = (endpoint: ServiceEndpoint) => {
    setEditingEndpoint(endpoint);
    setIsEditEndpointModalOpen(true);
  };

  const updateServicePartial = async (payload: UpdateServiceDtoLocal) => {
    if (!service) return;
    
    // Send only the fields that are being updated (partial update)
    const updatePayload: API.UpdateServiceDto = {};
    
    if (payload.name !== undefined) updatePayload.name = payload.name;
    if (payload.description !== undefined) updatePayload.description = payload.description;
    if (payload.version !== undefined) updatePayload.version = payload.version;
    if (payload.status !== undefined) updatePayload.status = payload.status;
    if (payload.tags !== undefined) updatePayload.tags = payload.tags;
    if (payload.consumers !== undefined) updatePayload.consumers = payload.consumers;
    
    // Update via store for seamless state management
    await updateServiceInStore(service.id, updatePayload);
  };


  const aggregateStatus = useMemo(() => {
    if (health.length > 0) {
      const anyDown = health.some(h => healthStatusLabel(h.currentHealthStatus) === 'critical');
      const anyUnknown = health.some(h => healthStatusLabel(h.currentHealthStatus) === 'unknown');
      if (anyDown) return 'critical';
      if (anyUnknown) return 'unknown';
      return 'healthy';
    }
    return service ? serviceStatusLabel(service.status) : 'unknown';
  }, [health, service]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="h-5 w-5 text-success" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-warning" />;
      case 'critical':
        return <AlertTriangle className="h-5 w-5 text-destructive" />;
      default:
        return <Clock className="h-5 w-5 text-muted-foreground" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'bg-success/10 text-success border-success/20';
      case 'warning':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'critical':
        return 'bg-destructive/10 text-destructive border-destructive/20';
      default:
        return 'bg-muted/10 text-muted-foreground border-muted/20';
    }
  };

  const getResourceIcon = (type: ServiceResource['type']) => {
    const icons = {
      repository: { icon: GitBranch, color: 'text-orange-600' },
      documentation: { icon: FileText, color: 'text-blue-600' },
      monitoring: { icon: BarChart3, color: 'text-green-600' },
      deployment: { icon: Globe, color: 'text-purple-600' },
      api: { icon: ExternalLink, color: 'text-yellow-600' },
      database: { icon: Database, color: 'text-red-600' },
      security: { icon: Shield, color: 'text-gray-600' },
      communication: { icon: MessageSquare, color: 'text-pink-600' },
      other: { icon: Globe, color: 'text-slate-600' },
    };
    const { icon: IconComponent, color } = icons[type] || icons.other;
    return <IconComponent className={`h-4 w-4 ${color}`} />;
  };

  const getResourceTypeColor = (type: ServiceResource['type']) => {
    const colors = {
      repository: 'bg-orange-500/10 text-orange-600 border-orange-500/20',
      documentation: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
      monitoring: 'bg-green-500/10 text-green-600 border-green-500/20',
      deployment: 'bg-purple-500/10 text-purple-600 border-purple-500/20',
      api: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
      database: 'bg-red-500/10 text-red-600 border-red-500/20',
      security: 'bg-gray-500/10 text-gray-600 border-gray-500/20',
      communication: 'bg-pink-500/10 text-pink-600 border-pink-500/20',
      other: 'bg-slate-500/10 text-slate-600 border-slate-500/20',
    };
    return colors[type] || colors.other;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" asChild>
            <Link to="/services">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Services
            </Link>
          </Button>
        </div>
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">Loading service…</CardContent>
        </Card>
      </div>
    );
  }

  if (err || !service) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" asChild>
            <Link to="/services">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Services
            </Link>
          </Button>
        </div>
        <ErrorAlert error={err} />
        <Card>
          <CardContent className="text-center py-12">
            <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">Service not found</h3>
            <p className="text-muted-foreground">
              {err || "The service you're looking for doesn't exist or has been removed."}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-3 md:space-y-6 px-2 md:px-0">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-col gap-3">
          <Button variant="outline" size="sm" asChild className="w-fit">
            <Link to="/services">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Services
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">{service.name}</h1>
            <p className="text-sm md:text-base text-muted-foreground">{service.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 self-start md:self-auto">
          {getStatusIcon(aggregateStatus)}
          <Badge variant="outline" className={getStatusColor(aggregateStatus)}>
            {aggregateStatus}
          </Badge>
        </div>
      </div>

      <div className="grid gap-4 md:gap-6 grid-cols-1 md:grid-cols-2">
        <Card>
          <CardHeader className="pb-2 md:pb-6">
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="flex items-center gap-2 text-base md:text-lg mb-1">
                <Package className="h-4 w-4 md:h-5 md:w-5 text-primary shrink-0" />
                <span className="truncate">Service Information</span>
              </CardTitle>
              {hasPermission('services:update') && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditInfoModalOpen(true)}
                  className="shrink-0"
                >
                  <Edit className="h-4 w-4 md:mr-2" />
                  <span className="hidden md:inline">Manage</span>
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-2 md:space-y-4">
            {/* Current Production Deployment */}
            {deployments.length > 0 && (() => {
              const prodEnv = environments.find(e => e.name.toLowerCase() === 'production' || e.name.toLowerCase() === 'prod');
              const prodDeployment = prodEnv ? deployments.find(d => d.environmentId === prodEnv.id) : deployments[0];
              
              if (prodDeployment) {
                return (
                  <div className="border border-muted rounded-lg p-3 bg-muted/30">
                    <div className="flex items-center gap-2 mb-2">
                      <GitBranch className="h-4 w-4 text-primary" />
                      <label className="text-sm font-medium">Current Deployment</label>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Version:</span>
                        <Badge variant="secondary" className="font-mono text-xs bg-info/10 text-info border-info/20">
                          {prodDeployment.version}
                        </Badge>
                      </div>
                      {prodDeployment.commitHash && (
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Commit:</span>
                          <span className="font-mono text-xs">{prodDeployment.commitHash}</span>
                        </div>
                      )}
                      {prodDeployment.deployedBy && (
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Deployed by:</span>
                          <span className="text-xs">{prodDeployment.deployedBy}</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Environment:</span>
                        <span className="text-xs">{prodDeployment.environmentName}</span>
                      </div>
                    </div>
                  </div>
                );
              }
              return null;
            })()}
            <div>
              <label className="text-sm font-medium text-muted-foreground">Type</label>
              <p className="text-sm font-medium capitalize">{typeLabel(service.type).replace('-', ' ')}</p>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-muted-foreground">Team</label>
              <div className="mt-1">
                <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20">{service.team || 'Unknown'}</Badge>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Created</label>
              <p className="text-sm">{formatIsoDate(service.createdAt)}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Last Updated</label>
              <p className="text-sm">{formatIsoDate(service.updatedAt)}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2 md:pb-6">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0 flex-1">
                <CardTitle className="flex items-center gap-2 text-base md:text-lg mb-1">
                  <LinkIcon className="h-4 w-4 md:h-5 md:w-5 text-primary shrink-0" />
                  <span className="truncate">Resources & Integrations</span>
                </CardTitle>
                <CardDescription className="text-xs md:text-sm mt-1">External links and integrations</CardDescription>
              </div>
              {hasPermission('services:update') && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsAddResourceModalOpen(true)}
                  className="shrink-0"
                >
                  <Plus className="h-4 w-4 md:mr-2" />
                  <span className="hidden md:inline">Add</span>
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {serviceResources.length > 0 ? (
              <div className="space-y-1.5 md:space-y-3">
                {serviceResources.map((resource) => (
                  <div key={resource.id} className="group flex items-start justify-between p-2.5 md:p-4 rounded-lg border bg-card hover:bg-accent/50 hover:border-primary/50 transition-all duration-200">
                    <div className="flex items-start gap-2 md:gap-4 flex-1 min-w-0">
                      <div className="flex-shrink-0 p-2 md:p-2.5 rounded-lg bg-primary/10">
                        {getResourceIcon(resource.type)}
                      </div>
                      <div className="flex-1 min-w-0 space-y-1">
                        <div className="flex items-center gap-1.5 md:gap-2 flex-wrap">
                          <span className="font-semibold text-xs md:text-sm truncate">{resource.name}</span>
                          <Badge variant="secondary" className="text-xs flex-shrink-0 border">
                            {resource.type}
                          </Badge>
                        </div>
                        {resource.description && (
                          <p className="text-xs text-muted-foreground line-clamp-2 md:truncate">{resource.description}</p>
                        )}
                        <a 
                          href={resource.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-start gap-1.5 text-xs font-mono text-muted-foreground hover:text-primary transition-colors group"
                          title={resource.url}
                        >
                          <span className="break-all line-clamp-2">{resource.url}</span>
                          <ExternalLink className="h-3 w-3 flex-shrink-0 mt-0.5" />
                        </a>
                      </div>
                    </div>
                    {hasPermission('services:update') && (
                      <div className="flex items-center gap-1 flex-shrink-0 ml-2 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditResource(resource)}
                          className="h-8 w-8 p-0"
                        >
                          <Edit className="h-3.5 w-3.5 md:h-4 md:w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            if (confirm('Are you sure you want to delete this resource?')) {
                              handleDeleteResource(resource.id);
                            }
                          }}
                          className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-3.5 w-3.5 md:h-4 md:w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <LinkIcon className="h-10 w-10 mx-auto mb-3 opacity-40" />
                <p className="text-sm font-medium mb-1">No resources configured</p>
                <p className="text-xs mb-3">Add external links and integrations for this service</p>
                {hasPermission('services:update') && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsAddResourceModalOpen(true)}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add your first resource
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-3 md:pb-6">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0 flex-1">
              <CardTitle className="flex items-center gap-2 text-base md:text-lg mb-1">
                <Globe className="h-4 w-4 md:h-5 md:w-5 text-primary shrink-0" />
                <span className="truncate">Environment Endpoints</span>
              </CardTitle>
              <CardDescription className="text-xs md:text-sm mt-1">Service URLs for different environments</CardDescription>
            </div>
            {hasPermission('services:update') && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsAddEndpointModalOpen(true)}
                className="shrink-0"
              >
                <Plus className="h-4 w-4 md:mr-2" />
                <span className="hidden md:inline">Add</span>
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {serviceEndpoints.length > 0 ? (
            <div className="space-y-3 md:space-y-6">
              {Array.from(new Set(serviceEndpoints.map(e => e.environment))).map((env) => {
                const envEndpoints = serviceEndpoints.filter(e => e.environment === env);
                if (envEndpoints.length === 0) return null;

                return (
                  <div key={env} className="space-y-1.5 md:space-y-3">
                    <div className="flex items-center gap-2 px-2 py-1.5 bg-muted/50 rounded-md">
                      <span className="text-xs md:text-sm font-bold capitalize">{env}</span>
                      <Badge variant="secondary" className="text-xs border">
                        {envEndpoints.length} endpoint{envEndpoints.length !== 1 ? 's' : ''}
                      </Badge>
                    </div>
                    <div className="space-y-1.5">
                      {envEndpoints.map((endpoint) => {
                        const icons = {
                          service: { 
                            icon: Globe, 
                            color: 'text-blue-600 dark:text-blue-400', 
                            bgColor: 'bg-blue-50 dark:bg-blue-950/30',
                            badgeColor: 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800'
                          },
                          version: { 
                            icon: FileText, 
                            color: 'text-purple-600 dark:text-purple-400',
                            bgColor: 'bg-purple-50 dark:bg-purple-950/30',
                            badgeColor: 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800'
                          },
                          health: { 
                            icon: Activity, 
                            color: 'text-green-600 dark:text-green-400',
                            bgColor: 'bg-green-50 dark:bg-green-950/30',
                            badgeColor: 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800'
                          },
                          swagger: { 
                            icon: FileText, 
                            color: 'text-orange-600 dark:text-orange-400',
                            bgColor: 'bg-orange-50 dark:bg-orange-950/30',
                            badgeColor: 'bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800'
                          },
                          logs: { 
                            icon: FileText, 
                            color: 'text-gray-600 dark:text-gray-400',
                            bgColor: 'bg-gray-50 dark:bg-gray-950/30',
                            badgeColor: 'bg-gray-100 dark:bg-gray-900/50 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-800'
                          },
                          other: { 
                            icon: Globe, 
                            color: 'text-slate-600 dark:text-slate-400',
                            bgColor: 'bg-slate-50 dark:bg-slate-950/30',
                            badgeColor: 'bg-slate-100 dark:bg-slate-900/50 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-800'
                          },
                        };
                        const { icon: IconComponent, color, bgColor, badgeColor } = icons[endpoint.type] || icons.other;
                        
                        return (
                          <div key={endpoint.id} className="group flex items-start justify-between p-2.5 md:p-4 border border-muted rounded-lg bg-card hover:bg-accent/50 transition-all duration-200">
                            <div className="flex items-start gap-2 md:gap-4 flex-1 min-w-0">
                              <div className={`p-2 md:p-2.5 rounded-lg ${bgColor} shrink-0`}>
                                <IconComponent className={`h-4 w-4 md:h-5 md:w-5 ${color}`} />
                              </div>
                              <div className="flex-1 min-w-0 space-y-1 md:space-y-1.5">
                                <div className="flex items-center gap-1.5 md:gap-2 flex-wrap">
                                  <span className="font-semibold text-xs md:text-sm truncate">
                                    {endpoint.name || endpoint.type.charAt(0).toUpperCase() + endpoint.type.slice(1)}
                                  </span>
                                  <Badge className={`text-xs border ${badgeColor} shrink-0`}>
                                    {endpoint.type}
                                  </Badge>
                                </div>
                                <a 
                                  href={endpoint.url} 
                                  target="_blank" 
                                  rel="noopener noreferrer" 
                                  className="flex items-start gap-1.5 text-xs font-mono text-muted-foreground hover:text-primary transition-colors group"
                                  title={endpoint.url}
                                >
                                  <span className="break-all line-clamp-2">{endpoint.url}</span>
                                  <ExternalLink className="h-3 w-3 flex-shrink-0 mt-0.5" />
                                </a>
                              </div>
                            </div>
                            {hasPermission('services:update') && (
                              <div className="flex items-center gap-1 flex-shrink-0 ml-2 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEditEndpoint(endpoint)}
                                  className="h-8 w-8 p-0"
                                >
                                  <Edit className="h-3.5 w-3.5 md:h-4 md:w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    if (confirm('Are you sure you want to delete this endpoint?')) {
                                      handleDeleteEndpoint(endpoint.id);
                                    }
                                  }}
                                  className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                >
                                  <Trash2 className="h-3.5 w-3.5 md:h-4 md:w-4" />
                                </Button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Globe className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm font-medium mb-1">No endpoints configured</p>
              <p className="text-xs mb-3">Add service URLs for different environments</p>
              {hasPermission('services:update') && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsAddEndpointModalOpen(true)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add your first endpoint
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-4 md:gap-6 md:grid-cols-2">
        <Card>
          <CardHeader className="pb-2 md:pb-6">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0 flex-1">
                <CardTitle className="text-base md:text-lg truncate mb-1">Dependencies</CardTitle>
                <CardDescription className="text-xs md:text-sm mt-1">Services and systems this service depends on</CardDescription>
              </div>
              {hasPermission('services:update') && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsManageDependenciesModalOpen(true)}
                  className="shrink-0"
                >
                  <Settings className="h-4 w-4 md:mr-2" />
                  <span className="hidden md:inline">Manage</span>
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {serviceDependencies.length > 0 ? (
              <div className="space-y-2">
                {serviceDependencies.map((dep) => (
                  <div key={dep.id} className="flex items-center justify-between p-2.5 border border-muted rounded-lg hover:bg-muted/50 transition-colors">
                    <div className="flex items-center gap-2 md:gap-3 flex-1 min-w-0">
                      <Package className="h-4 w-4 text-muted-foreground shrink-0" />
                      <div className="flex-1 min-w-0">
                        <Link 
                          to={`/services/${dep.dependsOnServiceId}`}
                          className="text-sm font-medium hover:text-primary transition-colors"
                        >
                          {dep.dependsOnServiceName}
                        </Link>
                        <div className="flex items-center gap-2 mt-0.5">
                          <Badge variant="outline" className="text-xs">
                            {dep.dependencyType}
                          </Badge>
                          {dep.isCritical && (
                            <Badge variant="destructive" className="text-xs">
                              Critical
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-muted-foreground">
                <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No dependencies configured</p>
                <p className="text-xs mt-1">
                  Manage dependencies in the <Link to="/dependency-graph" className="text-primary hover:underline">Dependency Graph</Link>
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div>
              <CardTitle>Dependents</CardTitle>
              <CardDescription>Services that depend on this service</CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            {serviceDependents.length > 0 ? (
              <div className="space-y-2">
                {serviceDependents.map((dep) => (
                  <div key={dep.id} className="flex items-center justify-between p-3 border border-muted rounded-lg hover:bg-muted/50 transition-colors">
                    <div className="flex items-center gap-3 flex-1">
                      <Package className="h-4 w-4 text-muted-foreground" />
                      <div className="flex-1">
                        <Link 
                          to={`/services/${dep.serviceId}`}
                          className="text-sm font-medium hover:text-primary transition-colors"
                        >
                          {dep.serviceName}
                        </Link>
                        <div className="flex items-center gap-2 mt-0.5">
                          <Badge variant="outline" className="text-xs">
                            {dep.dependencyType}
                          </Badge>
                          {dep.isCritical && (
                            <Badge variant="destructive" className="text-xs">
                              Critical
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-muted-foreground">
                <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No services depend on this service</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Tags</CardTitle>
                <CardDescription>Service categorization and metadata</CardDescription>
              </div>
              {hasPermission('services:update') && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditingTags(!isEditingTags)}
                >
                  {isEditingTags ? 'Stop Editing' : 'Edit'}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {isEditingTags ? (
              <TagInput
                tags={service.tags ?? []}
                onTagsChange={async (newTags) => {
                  try {
                    await updateServicePartial({ tags: newTags });
                  } catch (error) {
                    console.error('Error updating tags:', error);
                  }
                }}
                placeholder="Add tags (e.g. frontend, api, production)"
              />
            ) : (
              <div className="flex flex-wrap gap-2">
                {service.tags && service.tags.length > 0 ? (
                  service.tags.map((tag, idx) => {
                    const colors = [
                      'bg-blue-500/10 text-blue-600 border-blue-500/20',
                      'bg-purple-500/10 text-purple-600 border-purple-500/20',
                      'bg-green-500/10 text-green-600 border-green-500/20',
                      'bg-orange-500/10 text-orange-600 border-orange-500/20',
                      'bg-pink-500/10 text-pink-600 border-pink-500/20',
                    ];
                    return (
                      <Badge key={tag} variant="outline" className={colors[idx % colors.length]}>
                        {tag}
                      </Badge>
                    );
                  })
                ) : (
                  <p className="text-muted-foreground">No tags assigned</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <EditServiceInfoModal
        open={isEditInfoModalOpen}
        onOpenChange={setIsEditInfoModalOpen}
        onServiceUpdated={fetchAll}
        service={{
          id: service.id,
          name: service.name ?? '',
          description: service.description ?? '',
          version: service.version ?? '',
          status: service.status,
          type: service.type,
          team: service.team ?? '',
          teamId: service.teamId,
          tags: service.tags ?? [],
          isLocked: service.isLocked ?? false,
          createdAt: formatIsoDate(service.createdAt),
          updatedAt: formatIsoDate(service.updatedAt),
        }}
      />

      <AddEditResourceModal
        open={isAddResourceModalOpen}
        onOpenChange={setIsAddResourceModalOpen}
        onSave={handleAddResource}
      />

      <AddEditResourceModal
        open={isEditResourceModalOpen}
        onOpenChange={(open) => {
          setIsEditResourceModalOpen(open);
          if (!open) setEditingResource(null);
        }}
        resource={editingResource}
        onSave={handleEditResource}
      />

      <AddEditEndpointModal
        open={isAddEndpointModalOpen}
        onOpenChange={setIsAddEndpointModalOpen}
        onSave={handleAddEndpoint}
      />

      <AddEditEndpointModal
        open={isEditEndpointModalOpen}
        onOpenChange={(open) => {
          setIsEditEndpointModalOpen(open);
          if (!open) setEditingEndpoint(null);
        }}
        endpoint={editingEndpoint}
        onSave={handleEditEndpoint}
      />

      <ManageServiceDependenciesModal
        open={isManageDependenciesModalOpen}
        onOpenChange={setIsManageDependenciesModalOpen}
        serviceId={service?.id || ''}
        serviceName={service?.name || ''}
        dependencies={serviceDependencies}
        onRefresh={fetchAll}
      />
    </div>
  );
};
